import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const TYPE_LABELS = {
  asset: 'Aktiv',
  liability: 'Passiv',
  equity: 'Egenkapital',
  income: 'Indtægt',
  expense: 'Udgift'
};

const STANDARD_ACCOUNTS = new Set([
  '1010', '1020', '1030', '1040', '1310', '1320',
  '2010', '2110', '2330', '2410', '2420', '2430', '2440', '2510', '2610', '2620', '2630', '2640', '2660', '2900',
  '5030', '5110',
  '6410', '6610', '6620',
  '7010', '7050', '7420', '7460', '7690',
]);

export default function Accounts() {
  const { apiFetch } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ account_number: '', name: '', type: 'expense' });
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');

  const load = () => {
    apiFetch('/api/accounts').then(setAccounts).catch(err => setError(err.message));
  };

  useEffect(() => { load(); }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    try {
      await apiFetch('/api/accounts', { method: 'POST', body: form });
      setShowModal(false);
      setForm({ account_number: '', name: '', type: 'expense' });
      load();
    } catch (err) {
      setError(err.message);
    }
    setSaving(false);
  };

  const startEdit = (account) => {
    setEditingId(account.id);
    setEditName(account.name);
  };

  const saveEdit = async (account) => {
    const trimmed = editName.trim();
    if (!trimmed || trimmed === account.name) {
      setEditingId(null);
      return;
    }
    try {
      await apiFetch(`/api/accounts/${account.id}`, {
        method: 'PUT',
        body: { name: trimmed, type: account.type, is_active: account.is_active }
      });
      setEditingId(null);
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Er du sikker på at du vil slette denne konto?')) return;
    try {
      await apiFetch(`/api/accounts/${id}`, { method: 'DELETE' });
      load();
    } catch (err) {
      alert(err.message);
    }
  };

  const grouped = {
    asset: accounts.filter(a => a.type === 'asset'),
    liability: accounts.filter(a => a.type === 'liability'),
    equity: accounts.filter(a => a.type === 'equity'),
    income: accounts.filter(a => a.type === 'income'),
    expense: accounts.filter(a => a.type === 'expense')
  };

  return (
    <div>
      <div className="page-header">
        <h1>Kontoplan</h1>
        <button className="btn btn-primary" onClick={() => { setError(''); setForm({ account_number: '', name: '', type: 'expense' }); setShowModal(true); }}>+ Ny konto</button>
      </div>

      {error && !showModal && <div className="alert alert-error" style={{marginBottom: '1rem'}}>{error}</div>}

      <div className="alert alert-success" style={{background: '#ebf8ff', color: '#2a4365', border: '1px solid #90cdf4', marginBottom: '1rem'}}>
        Kontoplanen følger Erhvervsstyrelsens officielle standardkontoplan.
      </div>

      {Object.entries(grouped).map(([type, accs]) => (
        accs.length > 0 && (
          <div key={type} className="report-section">
            <h3>{TYPE_LABELS[type]}er</h3>
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th>Kontonr.</th>
                    <th>Navn</th>
                    <th>Type</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {accs.map(a => (
                    <tr key={a.id}>
                      <td>{a.account_number}</td>
                      <td>
                        {editingId === a.id ? (
                          <input className="form-control" value={editName}
                            onChange={e => setEditName(e.target.value)}
                            onBlur={() => saveEdit(a)}
                            onKeyDown={e => {
                              if (e.key === 'Enter' || e.key === 'Tab') { e.preventDefault(); saveEdit(a); }
                              if (e.key === 'Escape') setEditingId(null);
                            }}
                            autoFocus />
                        ) : (
                          <span onClick={() => startEdit(a)}
                            style={{cursor: 'pointer', borderBottom: '1px dashed var(--gray-400)'}}
                            title="Klik for at redigere">{a.name}</span>
                        )}
                      </td>
                      <td>{TYPE_LABELS[a.type]}</td>
                      <td className="text-right">
                        {!STANDARD_ACCOUNTS.has(a.account_number) && (
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(a.id)}>Slet</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )
      ))}

      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()}>
            <h2>Ny konto</h2>
            {error && <div className="alert alert-error">{error}</div>}
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>Kontonummer</label>
                  <input className="form-control" value={form.account_number}
                    onChange={e => setForm({...form, account_number: e.target.value})} required />
                </div>
                <div className="form-group">
                  <label>Type</label>
                  <select className="form-control" value={form.type}
                    onChange={e => setForm({...form, type: e.target.value})}>
                    {Object.entries(TYPE_LABELS).map(([k,v]) => (
                      <option key={k} value={k}>{v}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form-group">
                <label>Navn</label>
                <input className="form-control" value={form.name}
                  onChange={e => setForm({...form, name: e.target.value})} required />
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setShowModal(false)}>Annuller</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Opretter...' : 'Opret'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
